// messaging.js — register the responder's FCM token with the backend, handle
// foreground push (data-only) so the app reacts even when in the foreground.

import { getToken, onMessage } from 'firebase/messaging';
import { httpsCallable } from 'firebase/functions';
import { getMessagingIfSupported, functions } from './firebase.js';
import {
  isNative, registerNativePush, listenNativeForeground, unregisterNativePush,
} from './native-push.js';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

let currentToken = null;
let unsubscribeForeground = null;

/**
 * Request notification permission, fetch an FCM token, register it with the backend.
 * Returns the token, or null if push isn't supported / permission denied.
 */
export async function registerForPush() {
  // Native app (Capacitor): the service-worker/VAPID path below doesn't exist
  // in the WebView — @capacitor/push-notifications handles FCM/APNs directly.
  if (isNative()) return registerNativePush();

  const messaging = await getMessagingIfSupported();
  if (!messaging) {
    console.info('[push] not supported in this browser');
    return null;
  }
  if (!VAPID_KEY || VAPID_KEY === 'paste-from-firebase-console') {
    console.warn('[push] VITE_FIREBASE_VAPID_KEY not set — generate one in Firebase console → Cloud Messaging');
    return null;
  }
  if (Notification.permission === 'denied') {
    console.warn('[push] notification permission was denied');
    return null;
  }
  if (Notification.permission !== 'granted') {
    const result = await Notification.requestPermission();
    if (result !== 'granted') return null;
  }

  // Make sure the SW is registered (Vite-PWA registers its own; we add Firebase's)
  let swReg;
  try {
    swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  } catch (err) {
    console.error('[push] SW register failed', err);
    return null;
  }

  try {
    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg });
    if (!token) return null;
    currentToken = token;
    await httpsCallable(functions, 'updateFcmToken')({ token, platform: detectPlatform() });
    return token;
  } catch (err) {
    console.error('[push] getToken failed', err);
    return null;
  }
}

/**
 * Listen for foreground push (data-only messages). Caller passes a handler
 * that decides what UI/audio to play.
 * Returns an unsubscribe function.
 */
export async function listenForeground(handler) {
  if (isNative()) return listenNativeForeground(handler);
  const messaging = await getMessagingIfSupported();
  if (!messaging) return () => {};
  unsubscribeForeground = onMessage(messaging, (payload) => {
    handler(payload?.data || {});
  });
  return unsubscribeForeground;
}

export async function unregisterPush() {
  if (isNative()) return unregisterNativePush();
  if (currentToken) {
    try {
      await httpsCallable(functions, 'removeFcmToken')({ token: currentToken });
    } catch (err) {
      console.warn('[push] removeFcmToken failed', err);
    }
    currentToken = null;
  }
  if (unsubscribeForeground) { unsubscribeForeground(); unsubscribeForeground = null; }
}

function detectPlatform() {
  const ua = navigator.userAgent || '';
  if (/Android/.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  return 'web';
}
